import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../shared/api/http';

export type PrimaryEmailStatus = 'pending' | 'verified';

export interface PrimaryEmail {
  id: string;
  email: string;
  status: PrimaryEmailStatus;
  otpExpiresAt: string | null;
  verifiedAt: string | null;
  createdAt: string;
}

export const forwardingKeys = {
  primary: ['forwarding', 'primary'] as const,
};

/** GET /forwarding/primary — текущий основной адрес (или null, если не привязан). */
export function usePrimaryEmail(enabled = true) {
  return useQuery({
    queryKey: forwardingKeys.primary,
    queryFn: () => request<PrimaryEmail | null>('/api/v1/forwarding/primary'),
    staleTime: 30_000,
    enabled,
  });
}

function useInvalidatePrimary() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: forwardingKeys.primary });
}

/** POST /forwarding/primary — привязать/сменить адрес и выслать OTP. */
export function useAddPrimaryEmail() {
  const invalidate = useInvalidatePrimary();
  return useMutation({
    mutationFn: (email: string) =>
      request<PrimaryEmail>('/api/v1/forwarding/primary', { method: 'POST', body: { email } }),
    onSuccess: () => void invalidate(),
  });
}

/** POST /forwarding/primary/verify — подтвердить адрес OTP-кодом. */
export function useVerifyPrimaryEmail() {
  const invalidate = useInvalidatePrimary();
  return useMutation({
    mutationFn: (code: string) =>
      request<PrimaryEmail>('/api/v1/forwarding/primary/verify', { method: 'POST', body: { code } }),
    onSuccess: () => void invalidate(),
  });
}

/** POST /forwarding/primary/resend — повторно выслать OTP-код. */
export function useResendPrimaryOtp() {
  const invalidate = useInvalidatePrimary();
  return useMutation({
    mutationFn: () =>
      request<PrimaryEmail>('/api/v1/forwarding/primary/resend', { method: 'POST' }),
    onSuccess: () => void invalidate(),
  });
}

/** DELETE /forwarding/primary — отвязать основной адрес. */
export function useRemovePrimaryEmail() {
  const invalidate = useInvalidatePrimary();
  return useMutation({
    mutationFn: () =>
      request<{ removed: boolean }>('/api/v1/forwarding/primary', { method: 'DELETE' }),
    onSuccess: () => void invalidate(),
  });
}
